import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';
import * as Yup from 'yup';
import { useFormik, Form, FormikProvider } from 'formik';
// material
import { Stack, TextField, Box, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Icon } from '@iconify/react';
import checkmarkCircleFill from '@iconify/icons-eva/checkmark-circle-2-fill';
// contexts
import { useAuth } from '../../../contexts/Auth';
import { SettingsContext } from '../../../contexts/Settings';
import { EventContext } from '../../../contexts/Event';
// services
import { updateEvent } from '../../../services/Event';

EventFormSeatsIO.propTypes = {
  event: PropTypes.object,
  handleNext: PropTypes.func.isRequired,
  handleBack: PropTypes.func.isRequired,
  edit: PropTypes.bool
};

export default function EventFormSeatsIO({ event, handleNext, handleBack, edit }) {
  const { authUser } = useAuth();
  const { authSettings } = useContext(SettingsContext);
  const { setEvent } = useContext(EventContext);

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const eventSeatsIOValues = {
    seatsio: {
      workspaceKey: event?.seatsio?.workspaceKey || '',
      eventKey: event?.seatsio?.eventKey || '',
      region: event?.seatsio?.region || ''
    }
  };

  const EventSeatsIOSchema = Yup.object().shape({
    seatsio: Yup.object({
      workspaceKey: Yup.string().required('Workspace Key is required'),
      eventKey: Yup.string().required('Event Key is required'),
      region: Yup.string().required('Region is required')
    })
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: eventSeatsIOValues,
    validationSchema: EventSeatsIOSchema,
    onSubmit: async (values) => {
      setIsSubmitted(false);
      setIsLoading(true);

      try {
        const idToken = await authUser.getIdToken();
        const headers = {
          Authorization: `Bearer ${idToken}`,
          'Account-Key': authSettings.server.path
        };
        const payload = {
          seatsio: {
            workspaceKey: values.seatsio.workspaceKey,
            eventKey: values.seatsio.eventKey,
            region: values.seatsio.region
          }
        };

        await updateEvent({ headers, payload, id: event._id });
        setEvent((event) => ({ ...event, ...payload }));

        setIsLoading(false);

        if (!edit) {
          handleNext();
        } else {
          setIsSubmitted(true);
        }
      } catch (err) {
        setIsLoading(false);
        console.error(err);
        alert('An error occurred! Please, try again.');
      }
    }
  });

  const { errors, touched, handleSubmit, getFieldProps } = formik;

  return (
    <Box>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Workspace Key*"
              {...getFieldProps('seatsio.workspaceKey')}
              error={Boolean(touched.seatsio?.workspaceKey && errors.seatsio?.workspaceKey)}
              helperText={touched.seatsio?.workspaceKey && errors.seatsio?.workspaceKey}
            />

            <TextField
              fullWidth
              label="Event Key*"
              {...getFieldProps('seatsio.eventKey')}
              error={Boolean(touched.seatsio?.eventKey && errors.seatsio?.eventKey)}
              helperText={touched.seatsio?.eventKey && errors.seatsio?.eventKey}
            />

            <TextField
              fullWidth
              label="Region*"
              {...getFieldProps('seatsio.region')}
              error={Boolean(touched.seatsio?.region && errors.seatsio?.region)}
              helperText={touched.seatsio?.region && errors.seatsio?.region}
            />

            <Box>
              {!edit && (
                <>
                  <Button size="large" onClick={handleBack} sx={{ mr: 1 }}>
                    Back
                  </Button>
                  <Button size="large" variant="outlined" onClick={handleNext} sx={{ mr: 1 }}>
                    Next
                  </Button>
                </>
              )}
              <LoadingButton
                size="large"
                type="submit"
                variant="contained"
                loading={isLoading}
                endIcon={isSubmitted && <Icon icon={checkmarkCircleFill} />}
              >
                Save
              </LoadingButton>
            </Box>
          </Stack>
        </Form>
      </FormikProvider>
    </Box>
  );
}
